/**
 * The password rule (api.md "Auth", open-decisions.md "Passwords"), shared by the server (which
 * enforces it) and the apps (which explain it before a request is sent).
 *
 * Length only — no composition rules. Counted in Unicode code points after NFKC normalisation, the
 * form the server hashes, so "é" typed two ways is one password and one character.
 */

/** At least this many characters. */
export const PASSWORD_MIN_LENGTH = 10;
/** At most this many characters (bounds the hashing input; any passphrase fits). */
export const PASSWORD_MAX_LENGTH = 256;

/** The form a password is hashed and measured in. */
export function normalisePassword(password: string): string {
  return password.normalize('NFKC');
}

/** Code points of the normalised password. */
export function passwordLength(password: string): number {
  return [...normalisePassword(password)].length;
}

/**
 * Why `password` cannot be used for the account `email`, as a sentence for the person — or null when
 * it can.
 */
export function passwordProblem(password: string, email?: string | null): string | null {
  const length = passwordLength(password);
  if (length < PASSWORD_MIN_LENGTH) return `A password needs at least ${PASSWORD_MIN_LENGTH} characters.`;
  if (length > PASSWORD_MAX_LENGTH) return `A password can have at most ${PASSWORD_MAX_LENGTH} characters.`;
  if (normalisePassword(password).trim() === '') return 'A password cannot be only spaces.';
  if (email && normalisePassword(password).trim().toLowerCase() === email.trim().toLowerCase()) {
    return 'Your e-mail address cannot be your password.';
  }
  return null;
}
